import { ColumnProps } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { IProductsOrder } from "../../../models/order/products-order/response";
import { ImCheckmark, ImCross } from "react-icons/im";
import {
  CheckOutlined,
  CopyOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import Table from "../../general/table-components/table";
import {
  Button,
  Tooltip,
  notification,
  theme,
  Table as AntTable,
  Tag,
} from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import EditProductModal from "../product-management/EditProductModal";
import BatchAllocationModal from "../product-management/BatchAllocationModal";
import { Permissions } from "../../../utils/Permissions/Permissions";
import OrderProductService from "../../../services/order-product";
import { httpclient } from "../../../services/http-client";
import EditBtn from "../../general/table-components/actions/edit-btn";
import DeleteBtn from "../../general/table-components/actions/delete-btn";
import Space from "../../general/antd/space";
import { Link } from "react-router-dom";
import {
  accountingToByUnit,
  countAndPartitionToByUnit,
  formatQuantityByUnit,
  toFiniteNumber,
} from "../../../utils/helpers/quantity";
import { DEFAULT_WEIGHTED_PRODUCT_TOLERANCE_GRAMS } from "../../../utils/helpers/constants";
import type { ICashCheckLine } from "../../../models/order/cash-check";
import styles from "./styles.module.scss";

const formatPrice = (value: unknown): string => {
  const amount = toFiniteNumber(value);
  return amount === undefined
    ? "-"
    : amount.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const normalizeBarcodes = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];

  const seen = new Set<string>();
  const barcodes: string[] = [];

  value.forEach((barcode) => {
    if (typeof barcode !== "string") return;

    const normalizedBarcode = barcode.trim();
    if (!normalizedBarcode || seen.has(normalizedBarcode)) return;

    seen.add(normalizedBarcode);
    barcodes.push(normalizedBarcode);
  });

  return barcodes;
};

interface BarcodeButtonsProps {
  barcodes: string[];
}

const BarcodeButtons: React.FC<BarcodeButtonsProps> = ({ barcodes }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [copiedBarcode, setCopiedBarcode] = useState<string | null>(null);
  const resetTimerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (resetTimerRef.current !== null) {
        window.clearTimeout(resetTimerRef.current);
      }
    },
    [],
  );

  const copyBarcode = async (barcode: string) => {
    try {
      await navigator.clipboard.writeText(barcode);
      setCopiedBarcode(barcode);

      if (resetTimerRef.current !== null) {
        window.clearTimeout(resetTimerRef.current);
      }

      resetTimerRef.current = window.setTimeout(() => {
        setCopiedBarcode((current) => (current === barcode ? null : current));
      }, 1500);

      notification.success({
        message: t("barcode_copied"),
        description: barcode,
      });
    } catch {
      notification.error({ message: t("barcode_copy_failed") });
    }
  };

  if (!barcodes.length) {
    return (
      <span
        className={styles.noBarcode}
        style={{ color: token.colorTextSecondary }}
      >
        {t("no_barcode")}
      </span>
    );
  }

  return (
    <div className={styles.barcodeList}>
      {barcodes.map((barcode) => {
        const isCopied = copiedBarcode === barcode;

        return (
          <Tooltip
            key={barcode}
            title={isCopied ? t("barcode_copied") : t("copy")}
          >
            <Button
              size="small"
              className={styles.barcodeButton}
              icon={isCopied ? <CheckOutlined /> : <CopyOutlined />}
              aria-label={`${t("copy")} ${barcode}`}
              onClick={() => copyBarcode(barcode)}
            >
              <span dir="ltr">{barcode}</span>
            </Button>
          </Tooltip>
        );
      })}
    </div>
  );
};

interface IProps {
  data: any[];
  orderId: number;
  onRefresh: () => void;
  disableMutations?: boolean;
  /** General setting; drives which weighted lines read as short. */
  weightedToleranceGrams?: number | null;
  cashCheckLines?: ICashCheckLine[];
}

const ProductsOrderTable: React.FC<IProps> = ({
  data,
  orderId,
  onRefresh,
  disableMutations = false,
  weightedToleranceGrams,
  cashCheckLines = [],
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [batchModalMode, setBatchModalMode] = useState<"add" | "update">("add");
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [selectedBatchContext, setSelectedBatchContext] = useState<{
    allocationId?: number;
    batchId: number;
    quantity?: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const userPermissions =
    localStorage.getItem("permisionStoroge")?.split(",") || [];
  const cashCheckLinesByProductOrderId = useMemo(
    () =>
      new Map(
        cashCheckLines.map((line) => [Number(line.productOrderId), line]),
      ),
    [cashCheckLines],
  );

  const getLineOrigin = (record: any): "store" | "customer" | "unknown" => {
    // Canonical backend field for order line origin.
    if (
      record?.isAddedFromStore === true ||
      record?.isAddedFromStore === 1 ||
      record?.isAddedFromStore === "1"
    ) {
      return "store";
    }
    if (
      record?.isAddedFromStore === false ||
      record?.isAddedFromStore === 0 ||
      record?.isAddedFromStore === "0"
    ) {
      return "customer";
    }

    const rawOrigin =
      record?.lineOrigin ??
      record?.origin ??
      record?.source ??
      record?.addedFrom ??
      record?.createdByType ??
      record?.createdBy;

    const normalizedOrigin =
      typeof rawOrigin === "string" ? rawOrigin.toLowerCase() : "";
    if (["store", "pharmacy", "admin", "staff"].includes(normalizedOrigin))
      return "store";
    if (["customer", "client", "user"].includes(normalizedOrigin))
      return "customer";

    const storeFlag =
      record?.isStoreOrigin ??
      record?.isAddedFromStore ??
      record?.addedFromStore ??
      record?.addedByStore ??
      record?.storeAdded ??
      record?.createdByStore;
    if (storeFlag === true || storeFlag === 1 || storeFlag === "1")
      return "store";

    const customerFlag =
      record?.isCustomerOrigin ??
      record?.isAddedFromCustomer ??
      record?.addedFromCustomer ??
      record?.customerAdded ??
      record?.createdByCustomer;
    if (customerFlag === true || customerFlag === 1 || customerFlag === "1")
      return "customer";

    return "unknown";
  };

  const isStoreOriginLine = (record: any) => getLineOrigin(record) === "store";

  const getCreatedByFullName = (record: any): string => {
    const createdBy = record?.createdBy;
    if (!createdBy) return "-";

    const fullName =
      createdBy?.fullName ||
      [createdBy?.firstName, createdBy?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();

    return fullName || (createdBy?.id ? `#${createdBy.id}` : "-");
  };

  // Check if any product has location change data
  const hasLocationChange = useMemo(() => {
    return data?.some((product: any) => product?.browsingPriceOnClient != null);
  }, [data]);

  const handleDelete = async (productOrderId: number) => {
    if (!productOrderId) {
      notification.error({
        message: t("error"),
        description: t("product_id_missing"),
      });
      return;
    }

    setLoading(true);
    try {
      const orderProductService = new OrderProductService(httpclient);
      const requestData = { productOrderId };
      await orderProductService.removeProduct(requestData);
      notification.success({
        message: t("success"),
        description: t("product_removed_successfully"),
      });
      onRefresh();
    } catch (error: any) {
      console.error("Error removing product:", error);
      if (!error?.response && !error?.errorType) {
        notification.error({
          message: t("error"),
          description: error?.message || t("failed_to_remove_product"),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record: any) => {
    setSelectedProduct(record);
    setEditModalVisible(true);
  };

  const openBatchModal = (record: any, mode: "add" | "update") => {
    setSelectedProduct(record);
    setBatchModalMode(mode);
    setSelectedBatchContext(null);
    setBatchModalVisible(true);
  };

  const openBatchUpdateModalForRow = (record: any, batchRow: any) => {
    const allocationId =
      batchRow?.allocationId === undefined || batchRow?.allocationId === null
        ? undefined
        : Number(batchRow.allocationId);

    if (!allocationId || Number.isNaN(allocationId)) {
      notification.error({
        message: t("validation_error"),
        description: t("batch_not_linked_to_order_line"),
      });
      return;
    }

    setSelectedProduct(record);
    setBatchModalMode("update");
    setSelectedBatchContext({
      allocationId,
      batchId: Number(batchRow?.batchId),
      quantity: getBatchWithdrawalByUnit(record, batchRow),
    });
    setBatchModalVisible(true);
  };

  const getRecordFactor = (record: any) =>
    record?.factorAccountingSystem ||
    record?.product?.unit?.factorAccountingSystem ||
    record?.product?.unit?.factor_accounting_system ||
    1;

  const getRecordDecimalPlaces = (record: any) =>
    record?.decimalPlaces ?? record?.product?.unit?.decimalPlaces;

  const formatRecordQuantity = (record: any, value: unknown) =>
    `${formatQuantityByUnit(
      value,
      getRecordDecimalPlaces(record),
      getRecordFactor(record),
    )} ${record?.product?.unit?.name || ""}`;

  const getOriginalRequestedQuantityByUnit = (record: IProductsOrder) =>
    record?.originalRequestedQuantityByUnit ??
    record?.baseQuantityByUnit ??
    accountingToByUnit(
      record?.originalRequestedQuantityAccounting ??
        record?.baseQuantityAccounting,
      getRecordFactor(record),
    ) ??
    countAndPartitionToByUnit(
      record?.baseCount ?? record?.count,
      record?.baseUnitPartition ?? record?.unitPartition,
    );

  const getReservedQuantityByUnit = (record: IProductsOrder) =>
    record?.reservedQuantityByUnit ??
    record?.quantityByUnit ??
    accountingToByUnit(
      record?.reservedQuantityAccounting ?? record?.quantityAccounting,
      getRecordFactor(record),
    ) ??
    countAndPartitionToByUnit(record?.count, record?.unitPartition);

  const getCashCheckLine = (record: IProductsOrder) =>
    cashCheckLinesByProductOrderId.get(Number(record.id));

  const getDeliveredQuantityAccounting = (record: IProductsOrder) => {
    const cashCheckLine = getCashCheckLine(record);
    const appliedWeight = toFiniteNumber(cashCheckLine?.actualQuantityPhysical);

    // The applied Cash Check weight is the authoritative quantity delivered to
    // the customer. Inventory shortage is reported separately and must not be
    // added to the customer's quantity gap.
    if (record?.product?.soldByUnit && appliedWeight !== undefined) {
      return appliedWeight;
    }

    return getAccountingQuantity(
      record,
      record?.reservedQuantityAccounting ?? record?.quantityAccounting,
      getReservedQuantityByUnit(record),
    );
  };

  const getDisplayedDeliveredQuantityByUnit = (record: IProductsOrder) => {
    const delivered = getDeliveredQuantityAccounting(record);
    return delivered === undefined
      ? getReservedQuantityByUnit(record)
      : accountingToByUnit(delivered, getRecordFactor(record));
  };

  /**
   * Requested vs delivered, compared in the physical accounting unit — grams for
   * weighted products, pieces for the rest. That is the basis the backend uses
   * when it decides between ACCOUNTING_FINALIZATION and CUSTOMER_ACCEPTED, so the
   * badge here matches why an order asked the customer for approval.
   * `unitPartition.percentage` is rounded for legacy compatibility and must never
   * drive this comparison.
   */
  const getAccountingQuantity = (
    record: IProductsOrder,
    accountingValue: unknown,
    byUnitValue: number | undefined,
  ) => {
    const accounting = toFiniteNumber(accountingValue);
    if (accounting !== undefined) return accounting;
    if (byUnitValue === undefined) return undefined;

    return byUnitValue * getRecordFactor(record);
  };

  const getQuantityDeviation = (record: IProductsOrder) => {
    // A store-added line has no customer-requested baseline to compare against.
    if (isStoreOriginLine(record)) return null;

    const requested = getAccountingQuantity(
      record,
      record?.originalRequestedQuantityAccounting ??
        record?.baseQuantityAccounting,
      getOriginalRequestedQuantityByUnit(record),
    );
    const delivered = getDeliveredQuantityAccounting(record);

    if (requested === undefined || delivered === undefined) return null;

    const isWeighted = Boolean(record?.product?.soldByUnit);
    // The tolerance covers weighted lines only — pieces are matched exactly.
    const tolerance = isWeighted
      ? Math.max(
          0,
          toFiniteNumber(weightedToleranceGrams) ??
            DEFAULT_WEIGHTED_PRODUCT_TOLERANCE_GRAMS,
        )
      : 0;

    const difference = delivered - requested;
    // Epsilon absorbs the float noise of a byUnit * factor fallback.
    if (Math.abs(difference) <= tolerance + 1e-6) return null;

    return { difference, isWeighted };
  };

  const formatDeviationAmount = (
    record: IProductsOrder,
    difference: number,
    isWeighted: boolean,
  ) => {
    const amount = Math.abs(difference);

    // Weighted deviations read clearest in grams; the doc is explicit that the
    // comparison stays in the physical unit rather than in percentages.
    if (isWeighted) {
      return `${Math.round(amount).toLocaleString()} ${t("gram")}`;
    }

    return formatRecordQuantity(
      record,
      accountingToByUnit(amount, getRecordFactor(record)),
    );
  };

  const getBatchWithdrawalByUnit = (record: any, row: any) =>
    row?.withdrawnQuantityByUnit ??
    row?.quantityByUnit ??
    accountingToByUnit(
      row?.withdrawnQuantityAccounting ?? row?.quantityAccounting,
      getRecordFactor(record),
    );

  const columns: ColumnProps<IProductsOrder>[] = [
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
      align: "center",
      minWidth: 120,
      render: (_, record) => {
        const originalProduct = record?.product;
        const effectiveProduct =
          record?.isAlternative && record?.alternativeProduct
            ? record.alternativeProduct
            : originalProduct;
        const productId = effectiveProduct?.id;
        const productName = effectiveProduct?.name ?? "-";
        const barcodes = normalizeBarcodes(effectiveProduct?.barcodes);

        const productLink = productId ? (
          <Link to={`/products/details/${productId}`}>{productName}</Link>
        ) : (
          productName
        );

        return (
          <div className={styles.productNameCell}>
            <div>{productLink}</div>
            {record?.isAlternative && originalProduct?.name ? (
              <span
                className={styles.alternativeContext}
                style={{ color: token.colorTextSecondary }}
              >
                {t("alternative_to", { product: originalProduct.name })}
              </span>
            ) : null}
            {record?.isAccepted ? <BarcodeButtons barcodes={barcodes} /> : null}
          </div>
        );
      },
    },
    {
      title: t("unit"),
      dataIndex: "unit",
      key: "unit",
      align: "center",
      minWidth: 100,
      render: (_, record) => {
        return record?.product?.unit?.name ?? "-";
      },
    },
    {
      title: t("requested_quantity_by_unit"),
      dataIndex: "required_info",
      key: "required_info",
      align: "center",
      minWidth: 180,
      render: (_: any, record: IProductsOrder) => {
        if (isStoreOriginLine(record)) {
          return <Tag color="green">{t("added_from_store")}</Tag>;
        }

        return formatRecordQuantity(
          record,
          getOriginalRequestedQuantityByUnit(record),
        );
      },
    },
    {
      title: t("current_reserved_quantity_by_unit"),
      dataIndex: "sent_info",
      key: "sent_info",
      align: "center",
      minWidth: 180,
      render: (_: any, record: IProductsOrder) => {
        const reserved = formatRecordQuantity(
          record,
          getDisplayedDeliveredQuantityByUnit(record),
        );
        const deviation = getQuantityDeviation(record);
        const inventoryShortage = toFiniteNumber(
          getCashCheckLine(record)?.shortageQuantityPhysical,
        );

        if (!deviation && !inventoryShortage) return reserved;

        const isShort = Boolean(deviation && deviation.difference < 0);
        const amount = deviation
          ? formatDeviationAmount(
              record,
              deviation.difference,
              deviation.isWeighted,
            )
          : null;
        const inventoryShortageAmount = inventoryShortage
          ? formatDeviationAmount(
              record,
              inventoryShortage,
              Boolean(record?.product?.soldByUnit),
            )
          : null;

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 4,
            }}
          >
            <span>{reserved}</span>
            {deviation && (
              <Tooltip
                title={
                  deviation.isWeighted
                    ? t("quantity_deviation_hint_weighted")
                    : t("quantity_deviation_hint_piece")
                }
              >
                <Tag
                  color={isShort ? "error" : "warning"}
                  style={{ margin: 0 }}
                >
                  {isShort
                    ? t("customer_quantity_shortage_tag", { amount })
                    : t("quantity_surplus_tag", { amount })}
                </Tag>
              </Tooltip>
            )}
            {inventoryShortageAmount && (
              <Tooltip title={t("inventory_shortage_hint")}>
                <Tag color="volcano" style={{ margin: 0 }}>
                  {t("inventory_shortage_tag", {
                    amount: inventoryShortageAmount,
                  })}
                </Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },

    {
      title: t("single_selling_price"),
      dataIndex: "single_selling_price",
      key: "single_selling_price",
      align: "center",
      minWidth: 150,
      render: (_, record) => {
        return formatPrice(record?.priceOnClient);
      },
    },
    // Location change columns - only shown when there's location change data
    ...(hasLocationChange
      ? [
          {
            title: t("browsing_price"),
            dataIndex: "browsingPriceOnClient",
            key: "browsingPriceOnClient",
            align: "center" as const,
            minWidth: 150,
            render: (_: any, record: IProductsOrder) => {
              return record?.browsingPriceOnClient?.toLocaleString() ?? "-";
            },
          },
          {
            title: t("product_price_difference"),
            dataIndex: "productPriceDifference",
            key: "productPriceDifference",
            align: "center" as const,
            minWidth: 150,
            render: (_: any, record: IProductsOrder) => {
              if (record?.productPriceDifference == null) return "-";
              const diff = record.productPriceDifference;
              return (
                <span
                  style={{
                    color:
                      diff > 0
                        ? token.colorError
                        : diff < 0
                          ? token.colorSuccess
                          : token.colorText,
                    fontWeight: 600,
                  }}
                >
                  {diff > 0 ? "+" : ""}
                  {diff.toLocaleString()}
                </span>
              );
            },
          },
        ]
      : []),
    {
      title: t("total_selling_price"),
      dataIndex: "total_selling_price",
      key: "total_selling_price",
      align: "center",
      minWidth: 150,
      render: (_, record) => {
        return formatPrice(record?.totalPriceOnClient);
      },
    },
    {
      title: t("store"),
      dataIndex: "store",
      key: "store",
      align: "center",
      render: (_, record) => {
        return record?.store?.name ?? "-";
      },
    },
    {
      title: t("accepted"),
      dataIndex: "isAccepted",
      align: "center",
      minWidth: 110,
      key: "isAccepted",
      render: (_, record) => {
        return (
          <>
            {record.isAccepted ? (
              <ImCheckmark color="green" />
            ) : (
              <ImCross color="red" />
            )}
          </>
        );
      },
    },
    {
      title: t("line_origin"),
      dataIndex: "line_origin",
      key: "line_origin",
      align: "center",
      minWidth: 130,
      render: (_: any, record: IProductsOrder) => {
        const origin = getLineOrigin(record);
        if (origin === "store") {
          return <Tag color="green">{t("added_from_store")}</Tag>;
        }
        if (origin === "customer") {
          return <Tag color="orange">{t("added_from_customer")}</Tag>;
        }
        return <Tag color="default">{t("unknown")}</Tag>;
      },
    },
    {
      title: t("single_pharmacy_price"),
      dataIndex: "price",
      key: "price",
      align: "center",
      minWidth: 150,
      render: (_, record) => {
        return formatPrice(record?.price);
      },
    },
    {
      title: t("total_pharmacy_price"),
      dataIndex: "totalPrice",
      key: "totalPrice",
      align: "center",
      minWidth: 150,
      render: (_, record) => {
        return formatPrice(record?.totalPrice);
      },
    },
    {
      title: t("alternative_product"),
      dataIndex: "alternative_product",
      key: "alternative_product",
      align: "center",
      minWidth: 100,
      render: (_, record) => {
        return record?.alternativeProduct?.name ?? "-";
      },
    },
    {
      title: t("nameSlice"),
      dataIndex: "nameSlice",
      key: "nameSlice",
      align: "center",
      minWidth: 100,
      render: (_, record) => {
        return record?.nameSlice ?? "-";
      },
    },
    {
      title: t("numberPoint"),
      dataIndex: "numberPoint",
      key: "numberPoint",
      align: "center",
      minWidth: 100,
      render: (_, record) => {
        return record?.numberPoint ?? "-";
      },
    },
    {
      title: t("need_recipe"),
      dataIndex: "recipeRequired",
      align: "center",
      minWidth: 110,
      key: "recipeRequired",
      render: (_, record) => {
        return (
          <>
            {record.recipeRequired ? (
              <ImCheckmark color="green" />
            ) : (
              <ImCross color="red" />
            )}
          </>
        );
      },
    },

    {
      title: t("actions"),
      width: 150,
      dataIndex: "",
      align: "center",
      fixed: "right",
      key: "x",
      render: (_: any, record: any) => {
        return (
          <Space>
            {userPermissions.includes(
              Permissions.CAN_ADMIN_UPDATE_ORDER_PRODUCT_QUANTITY,
            ) && (
              <Tooltip
                title={
                  disableMutations
                    ? t("cannot_modify_order_in_trip")
                    : undefined
                }
              >
                <span>
                  <EditBtn
                    onClick={() => handleEdit(record)}
                    aria-label={`${t("edit")} ${record?.product?.name}`}
                    disabled={disableMutations}
                  />
                </span>
              </Tooltip>
            )}
            {userPermissions.includes(
              Permissions.CAN_ADMIN_UPDATE_ORDER_PRODUCT_QUANTITY,
            ) && (
              <Tooltip
                title={
                  disableMutations
                    ? t("cannot_modify_order_in_trip")
                    : undefined
                }
              >
                <Button
                  size="small"
                  onClick={() => openBatchModal(record, "add")}
                  disabled={disableMutations}
                  shape="circle"
                  icon={<PlusOutlined />}
                  aria-label={`${t("add_from_batch")} ${record?.product?.name}`}
                ></Button>
              </Tooltip>
            )}
            {userPermissions.includes(
              Permissions.CAN_ADMIN_REMOVE_ORDER_PRODUCT,
            ) && (
              <Tooltip
                title={
                  disableMutations
                    ? t("cannot_modify_order_in_trip")
                    : !isStoreOriginLine(record)
                      ? t("cannot_remove_customer_origin_line")
                      : undefined
                }
              >
                <span>
                  <DeleteBtn
                    onConfirm={() => {
                      if (!disableMutations && isStoreOriginLine(record)) {
                        handleDelete(record.id);
                      }
                    }}
                    loading={loading || disableMutations}
                    disabled={disableMutations || !isStoreOriginLine(record)}
                    aria-label={`${t("delete")} ${record?.product?.name}`}
                  />
                </span>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Table<any>
        rowKey="id"
        columns={columns}
        dataSource={data ?? []}
        size="small"
        scroll={{
          y: data && data.length > 10 ? 400 : undefined,
          x: "max-content",
        }}
        expandable={{
          rowExpandable: (record) => !!record.batchWithdrawals?.length,
          expandedRowRender: (record) => (
            <AntTable
              rowKey={(row: any) => String(row.allocationId ?? row.batchId)}
              size="small"
              pagination={false}
              dataSource={record.batchWithdrawals ?? []}
              columns={[
                {
                  title: t("batch_number"),
                  dataIndex: "batchNumber",
                  key: "batchNumber",
                  render: (val: string, row: any) => val ?? `#${row.batchId}`,
                },
                {
                  title: t("purchase_date"),
                  dataIndex: "purchaseDate",
                  key: "purchaseDate",
                  render: (val: string) => val ?? "-",
                },
                {
                  title: t("expiration_date"),
                  dataIndex: "expirationDate",
                  key: "expirationDate",
                  render: (val: string) => val ?? "-",
                },
                {
                  title: t("withdrawn_quantity"),
                  dataIndex: "withdrawnQuantityByUnit",
                  key: "withdrawnQuantityByUnit",
                  render: (_val: number, row: any) =>
                    formatRecordQuantity(
                      record,
                      getBatchWithdrawalByUnit(record, row),
                    ),
                },
                {
                  title: t("line_origin"),
                  key: "line_origin",
                  render: (_: any, row: any) => {
                    const origin = getLineOrigin(row);
                    if (origin === "store") {
                      return <Tag color="green">{t("added_from_store")}</Tag>;
                    }
                    if (origin === "customer") {
                      return (
                        <Tag color="orange">{t("added_from_customer")}</Tag>
                      );
                    }
                    return <Tag color="default">{t("unknown")}</Tag>;
                  },
                },
                {
                  title: t("created_by"),
                  key: "createdBy",
                  render: (_: any, row: any) =>
                    getLineOrigin(row) === "store"
                      ? getCreatedByFullName(row)
                      : "-",
                },
                {
                  title: t("actions"),
                  key: "batch_actions",
                  render: (_: any, batchRow: any) =>
                    userPermissions.includes(
                      Permissions.CAN_ADMIN_UPDATE_ORDER_PRODUCT_QUANTITY,
                    ) ? (
                      <Tooltip
                        title={
                          disableMutations
                            ? t("cannot_modify_order_in_trip")
                            : undefined
                        }
                      >
                        <Button
                          size="small"
                          onClick={() =>
                            openBatchUpdateModalForRow(record, batchRow)
                          }
                          disabled={disableMutations}
                          shape="circle"
                          icon={<EditOutlined />}
                          aria-label={`${t("adjust_this_batch")} ${record?.product?.name}`}
                        ></Button>
                      </Tooltip>
                    ) : null,
                },
              ]}
            />
          ),
        }}
      />

      {selectedProduct && (
        <EditProductModal
          visible={editModalVisible}
          onCancel={() => {
            setEditModalVisible(false);
            setSelectedProduct(null);
          }}
          onSuccess={() => {
            setEditModalVisible(false);
            setSelectedProduct(null);
            onRefresh();
          }}
          orderId={orderId}
          orderProduct={selectedProduct}
        />
      )}

      {selectedProduct && (
        <BatchAllocationModal
          visible={batchModalVisible}
          mode={batchModalMode}
          productOrderId={selectedProduct?.id}
          productId={selectedProduct?.product?.id}
          storeId={selectedProduct?.store?.id || selectedProduct?.store_id}
          withdrawals={selectedProduct?.batchWithdrawals}
          decimalPlaces={getRecordDecimalPlaces(selectedProduct)}
          factorAccountingSystem={getRecordFactor(selectedProduct)}
          quantityStepByUnit={
            selectedProduct?.quantityStepByUnit ??
            selectedProduct?.product?.unit?.quantityStepByUnit
          }
          unitName={selectedProduct?.product?.unit?.name}
          onCancel={() => {
            setBatchModalVisible(false);
            setSelectedProduct(null);
            setSelectedBatchContext(null);
          }}
          onSuccess={() => {
            setBatchModalVisible(false);
            setSelectedProduct(null);
            setSelectedBatchContext(null);
            onRefresh();
          }}
          initialBatchId={selectedBatchContext?.batchId}
          initialAllocationId={selectedBatchContext?.allocationId}
          initialQuantity={selectedBatchContext?.quantity}
        />
      )}
    </>
  );
};

export default ProductsOrderTable;
